// A sortable table header cell.
//
// The header is a real <button> inside the <th>, not a click handler on the <th>:
// that is what makes the column reachable by keyboard and announced as pressable.
// `aria-sort` lives on the <th> (where the spec puts it) and is driven by the
// same hook that does the sorting, so the announced state cannot drift from the
// rendered order.
import type { ReactNode } from "react";

import "./table.css";

export interface SortableThProps<K extends string> {
  /** Column id — the key into the table's sort accessors. */
  sortKey: K;
  children: ReactNode;
  ariaSort: (key: K) => "ascending" | "descending" | "none";
  onToggle: (key: K) => void;
  /** Right-align, for numeric columns. */
  numeric?: boolean;
}

export function SortableTh<K extends string>({
  sortKey,
  children,
  ariaSort,
  onToggle,
  numeric = false,
}: SortableThProps<K>) {
  const state = ariaSort(sortKey);
  return (
    <th aria-sort={state} className={numeric ? "rc-num" : undefined}>
      <button
        type="button"
        className={`rc-sort${state !== "none" ? " rc-sort--active" : ""}`}
        onClick={() => onToggle(sortKey)}
      >
        <span>{children}</span>
        {/* Decorative: `aria-sort` on the <th> already carries the state to a
            screen reader, so this must not be announced a second time. */}
        <span aria-hidden="true" className="rc-sort__caret" data-direction={state}>
          {state === "ascending" ? "▲" : state === "descending" ? "▼" : "⇅"}
        </span>
      </button>
    </th>
  );
}
